/* eslint-disable @typescript-eslint/restrict-plus-operands */
import dayjs from 'dayjs';
import type { productionAsset } from 'types/production.types';
import { convertsecondsToHours } from 'views/Utilization/utils';
import type { DownTimeData } from 'store/SquaresStore/DashboardSquaresState.slice';

export const productionValueMappings = {
  'Tonnage Moved': {
    value: 'avgTonDay',
    diff: 'avgTonDay_percentage_diff',
  },
  'Tonnage Per Hour': {
    value: 'avgLoadPerHour',
    diff: 'avgLoadPerHour_percentage_diff',
  },
  'Cycle Time': {
    value: 'cycle_time',
    diff: 'cycle_time_percentage_diff',
  },
} as const;

export const getProductionValuesFromData = (
  data: any,
  groupingType: keyof typeof productionValueMappings
) => {
  const mapping = productionValueMappings[groupingType];

  return {
    productionValue: data[mapping.value] ?? 0,
    productionValueDiff: data[mapping.diff] ?? 0,
  };
};

export type ProductionGroupingType = keyof typeof productionValueMappings;

export const customerAssetMinProduction = {
  buz: 110,
  other: 100,
};

export function checkAssetPerformance(
  customerCode: string,
  assets: productionAsset[]
): string {
  if (assets.length === 0) {
    return `No Suggestions`;
  }
  const minThreshold =
    customerAssetMinProduction[
      customerCode as keyof typeof customerAssetMinProduction
    ] || customerAssetMinProduction.other;

  const underperformingAssets = assets.filter((asset) => {
    const avgMaxLoad =
      typeof asset.avg_max_load_per_day === 'string'
        ? parseFloat(asset.avg_max_load_per_day)
        : asset.avg_max_load_per_day;

    return avgMaxLoad < minThreshold;
  });

  if (underperformingAssets.length === 0) {
    return `All assets are meeting or exceeding the minimum production threshold of ${minThreshold} tons per day.`;
  }

  const assetList = underperformingAssets
    .map(
      (asset) =>
        `${asset.bumper_id} (${
          typeof asset.avg_max_load_per_day === 'string'
            ? parseFloat(asset.avg_max_load_per_day).toFixed(2)
            : asset.avg_max_load_per_day.toFixed(2)
        } tons/day)`
    )
    .join('\n');

  return `Warning: ${underperformingAssets.length} asset(s) performed below the minimum production threshold of ${minThreshold} tons per day.\nUnderperforming assets:\n${assetList}`;
}

export const utilizationValueMappings = {
  Utilization: {
    value: 'fleet_avg_utilization_by_whole_day',
    diff: 'fleet_avg_utilization_by_whole_day_diff',
  },
  'Fuel Consumption': {
    value: 'fleet_avg_fuel_consumption_l',
    diff: 'fleet_avg_fuel_consumption_l_diff',
  },
  'Excess Idling': {
    value: 'fleet_avg_idling_duration',
    diff: 'fleet_avg_idling_duration_diff',
  },
  'Downtime Hours': {
    value: '',
    diff: '',
  },
} as const;

export const getUtilizationValuesFromData = (
  data: any,
  groupingType: keyof typeof utilizationValueMappings,
  downTimeData: DownTimeData
) => {
  const { current, previous } = downTimeData;
  const mapping = utilizationValueMappings[groupingType];

  let utilizationValue = data[mapping.value] ?? 0;
  let utilizationValueDiff = data[mapping.diff] ?? 0;

  switch (groupingType) {
    case 'Utilization':
      utilizationValue = utilizationValue * 100;
      break;
    case 'Excess Idling':
      utilizationValue = convertsecondsToHours(utilizationValue);
      break;
    case 'Downtime Hours': {
      utilizationValue =
        current.reduce(
          (accumulator, currentValue) => accumulator + currentValue.pm_duration,
          0
        ) /
        (60 * 60 * 60);

      const prevValue =
        previous.reduce(
          (accumulator, currentValue) => accumulator + currentValue.pm_duration,
          0
        ) /
        (60 * 60 * 60);

      if (prevValue === 0) {
        if (utilizationValue === 0) {
          utilizationValueDiff = 0;
        } else {
          utilizationValueDiff = 100;
        }
      } else {
        utilizationValueDiff =
          ((utilizationValue - prevValue) / prevValue) * 100;
      }

      break;
    }
  }

  return {
    utilizationValue,
    utilizationValueDiff,
  };
};

export function filterDtcByWords(data: any[], words: string[], keys: string[]) {
  const pattern = new RegExp(`(${words.join('|')})`, 'i');

  return data.filter((item) => {
    const keysToCheck = keys && keys.length > 0 ? keys : Object.keys(item);

    return keysToCheck.some((key) => {
      const val = item[key];
      return typeof val === 'string' && pattern.test(val.toLowerCase());
    });
  });
}

export function filterLastDaysActive(data: any[]) {
  const now = dayjs.utc();
  return data.filter((item) => {
    const alertStart = dayjs.utc(item.alertStartTs);
    return alertStart.isAfter(now.subtract(1, 'day')) && item.isActive;
  });
}

export function mergeDtcEvents(
  utilizationDtcEvents: any[],
  productionDtcEvents: any[],
  fuelDtcEvents: any[]
) {
  const addMetric = (arr: any[], metric: string) =>
    arr.map((item) => ({ ...item, dtcMetric: metric }));

  return [
    ...addMetric(utilizationDtcEvents, 'Utilization'),
    ...addMetric(productionDtcEvents, 'Production'),
    ...addMetric(fuelDtcEvents, 'Fuel Usage'),
  ];
}

const prefixesToTrimFromDTC = ['CDL', 'j1939', 'j1708', 'kj1939'];
const suffixesToTrimFromDTC = ['DTC'];

/**
 * Cleans a DTC type string by trimming known prefixes and suffixes.
 * @param rawValue string to clean
 */
export function cleanDTCType(rawValue: string | undefined | null): string {
  if (typeof rawValue !== 'string') return '';

  let value = rawValue;

  for (const prefix of prefixesToTrimFromDTC) {
    if (value.toLowerCase().startsWith(prefix.toLowerCase())) {
      value = value.slice(prefix.length).trim();
      break;
    }
  }

  for (const suffix of suffixesToTrimFromDTC) {
    if (value.toLowerCase().endsWith(suffix.toLowerCase())) {
      value = value.slice(0, -suffix.length).trim();
      break;
    }
  }

  return value;
}

export function generateAssetRecommendations(arg: any) {
  const data = arg[0];

  if (!data) {
    return {};
  }

  function formatTime(seconds: number) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.round(seconds % 60);

    const parts = [];
    if (hours > 0) parts.push(`${hours} hr`);
    if (minutes > 0) parts.push(`${minutes} min`);
    if (secs > 0) parts.push(`${secs} sec`);

    return parts.join(' ') || '0 sec';
  }

  function calculatePercentage(value: number, total: number) {
    if (total === 0) return 0;
    return ((value / total) * 100).toFixed(2);
  }

  const assetOutliers: any = {};

  data.outlier_trips_based_on_time.forEach((trip: any) => {
    if (!assetOutliers[trip.device]) {
      assetOutliers[trip.device] = {
        device: trip.device,
        outlierTrips: [],
        totalOutlierTime: 0,
        totalOutlierFuel: 0,
        totalOutlierDistance: 0,
      };
    }

    assetOutliers[trip.device].outlierTrips.push(trip);
    assetOutliers[trip.device].totalOutlierTime += trip.tripTime;
    assetOutliers[trip.device].totalOutlierFuel += trip.fuel_consumed;
    assetOutliers[trip.device].totalOutlierDistance += trip.travelDistance;
  });

  const assetTotals: any = {};

  data.trips_more_than_average_time.forEach((trip: any) => {
    if (!assetTotals[trip.device]) {
      assetTotals[trip.device] = {
        device: trip.device,
        totalTrips: 0,
        totalTime: 0,
        totalFuel: 0,
        totalDistance: 0,
        excessIdlingTrips: 0,
        excessIdlingTime: 0,
      };
    }

    assetTotals[trip.device].totalTrips++;
    assetTotals[trip.device].totalTime += trip.tripTime;
    assetTotals[trip.device].totalFuel += trip.fuel_consumed;
    assetTotals[trip.device].totalDistance += trip.travelDistance;

    if (trip.tripTime > data.average_time_per_trip) {
      assetTotals[trip.device].excessIdlingTrips++;
      assetTotals[trip.device].excessIdlingTime +=
        trip.tripTime - data.average_time_per_trip;
    }
  });

  data.trips_other_than_outliers_based_on_time.forEach((trip: any) => {
    if (!assetTotals[trip.device]) {
      assetTotals[trip.device] = {
        device: trip.device,
        totalTrips: 0,
        totalTime: 0,
        totalFuel: 0,
        totalDistance: 0,
        excessIdlingTrips: 0,
        excessIdlingTime: 0,
      };
    }

    assetTotals[trip.device].totalTrips++;
    assetTotals[trip.device].totalTime += trip.tripTime;
    assetTotals[trip.device].totalFuel += trip.fuel_consumed;
    assetTotals[trip.device].totalDistance += trip.travelDistance;
  });

  const assetRecommendations: any = {};

  const allDevices = new Set([
    ...Object.keys(assetOutliers),
    ...Object.keys(assetTotals),
  ]);

  allDevices.forEach((device) => {
    const outlier = assetOutliers[device] || {
      outlierTrips: [],
      totalOutlierTime: 0,
      totalOutlierFuel: 0,
      totalOutlierDistance: 0,
    };
    const totals = assetTotals[device] || {
      totalTrips: 0,
      totalTime: 0,
      totalFuel: 0,
      totalDistance: 0,
      excessIdlingTrips: 0,
      excessIdlingTime: 0,
    };

    // Use the global fuel saving percentage as a baseline, or calculate from outlier vs normal trip efficiency
    const globalFuelSavingRate = data.potential_fuel_saving_percentage || 0;
    const potentialFuelSavings = totals.totalFuel * globalFuelSavingRate;
    const fuelSavingsPercentage = calculatePercentage(
      potentialFuelSavings,
      totals.totalFuel
    );

    const timeSavingsPercentage =
      totals.totalTime > 0
        ? calculatePercentage(totals.excessIdlingTime, totals.totalTime)
        : 0;

    const avgTripTime =
      totals.totalTrips > 0
        ? totals.totalTime / totals.totalTrips
        : data.average_time_per_trip;
    const potentialAdditionalTrips = Math.floor(
      totals.excessIdlingTime / avgTripTime
    );

    const avgTonnagePerTrip =
      data.total_trips > 0 ? data.total_max_load_ton / data.total_trips : 0;
    const currentTonnage = totals.totalTrips * avgTonnagePerTrip;
    const potentialTonnageIncrease =
      potentialAdditionalTrips * avgTonnagePerTrip;
    const maxPossibleTonnage = currentTonnage + potentialTonnageIncrease;

    assetRecommendations[device] = {
      device,
      recommendations: [
        `Potential Fuel savings: **${potentialFuelSavings.toFixed(
          2
        )} gal** (**${fuelSavingsPercentage}%** of **${totals.totalFuel.toFixed(
          2
        )} gal** Fuel can be saved)`,
        `Potential Time Savings: **${formatTime(
          totals.excessIdlingTime
        )}** (**${timeSavingsPercentage}%** of **${formatTime(
          totals.totalTime
        )}** total trip time was excess idling)`,
        `Potential tonnage increase based on asset specifications: **${potentialTonnageIncrease.toFixed(
          2
        )} Tons** (Current tonnage: **${currentTonnage.toFixed(
          2
        )} Tons**, Maximum possible: **${maxPossibleTonnage.toFixed(
          2
        )} Tons**)`,
        `Potential trips that can be increased based on excess idling are **${potentialAdditionalTrips}**`,
        `Number of Outlier Trips: **${outlier.outlierTrips.length}**`,
      ],
    };
  });

  return assetRecommendations;
}
